import React, { useState, useCallback, useEffect } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';

// ============ ANIMATIONS ============
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideInDown = keyframes`
  from { 
    opacity: 0;
    transform: translateY(-12px);
  }
  to { 
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideOutUp = keyframes`
  from { 
    opacity: 1;
    transform: translateY(0);
  }
  to { 
    opacity: 0;
    transform: translateY(-12px);
  }
`;

// ============ STYLED COMPONENTS ============
const PanelWrapper = styled.div`
  position: fixed;
  top: 72px;
  right: 0;
  width: 420px;
  max-height: calc(100vh - 72px);
  background: white;
  border-left: 1px solid #e2e8f0;
  border-bottom: 1px solid #e2e8f0;
  box-shadow: -4px 4px 12px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  z-index: 999;
  animation: ${slideInDown} 300ms ease-out;

  @media (max-width: 768px) {
    width: 100%;
    right: 0;
    left: 0;
  }
`;

const PanelHeader = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f8fafc;

  h2 {
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
    color: #1e293b;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .clear-btn {
    background: none;
    border: none;
    color: #64748b;
    cursor: pointer;
    font-size: 0.85rem;
    transition: color 200ms;

    &:hover {
      color: #475569;
    }
  }

  .close-btn {
    background: none;
    border: none;
    cursor: pointer;
    color: #94a3b8;
    font-size: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: color 200ms;

    &:hover {
      color: #64748b;
    }
  }
`;

const PanelContent = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;

    &:hover {
      background: #94a3b8;
    }
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 32px 20px;
  text-align: center;

  .empty-icon {
    width: 48px;
    height: 48px;
    margin-bottom: 12px;
    opacity: 0.3;
  }

  .empty-title {
    font-size: 0.95rem;
    font-weight: 600;
    color: #64748b;
    margin-bottom: 4px;
  }

  .empty-text {
    font-size: 0.85rem;
    color: #94a3b8;
  }
`;

const NotificationItemWrapper = styled.div`
  animation: ${props => props.$isClosing ? slideOutUp : slideInDown} 300ms ease-out forwards;
`;

const NotificationItemContainer = styled.div`
  padding: 14px 16px;
  border-bottom: 1px solid #e2e8f0;
  cursor: pointer;
  transition: all 200ms;
  background: ${props => props.$isRead ? '#ffffff' : '#f0f9ff'};

  &:hover {
    background: ${props => props.$isRead ? '#f8fafc' : '#eff6ff'};
  }

  &.unread {
    border-left: 3px solid #3b82f6;
    padding-left: 13px;
  }

  &.success {
    border-left: 3px solid #10b981;
  }

  &.error {
    border-left: 3px solid #ef4444;
  }

  &.warning {
    border-left: 3px solid #f59e0b;
  }

  &.info {
    border-left: 3px solid #3b82f6;
  }
`;

const NotificationHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
`;

const NotificationTitleWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const NotificationIcon = styled.div`
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 2px;

  color: ${props => {
    switch (props.$type) {
      case 'success': return '#10b981';
      case 'error': return '#ef4444';
      case 'warning': return '#f59e0b';
      case 'info': return '#3b82f6';
      default: return '#64748b';
    }
  }};
`;

const NotificationTitle = styled.h3`
  margin: 0;
  font-size: 0.9rem;
  font-weight: 600;
  color: #1e293b;
`;

const NotificationTime = styled.span`
  font-size: 0.75rem;
  color: #94a3b8;
  white-space: nowrap;
`;

const NotificationMessage = styled.p`
  margin: 6px 0 8px 0;
  font-size: 0.85rem;
  color: #475569;
  line-height: 1.4;
`;

const NotificationDetails = styled.div`
  background: #f8fafc;
  padding: 10px 12px;
  border-radius: 6px;
  margin-bottom: 10px;
  font-size: 0.8rem;
  color: #475569;
  border-left: 2px solid #cbd5e1;

  .detail-label {
    font-weight: 600;
    color: #334155;
    margin-bottom: 2px;
  }

  .detail-value {
    color: #64748b;
    word-break: break-word;
  }
`;

const NotificationActions = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button`
  flex: 1;
  padding: 6px 10px;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 200ms;
  background: white;
  color: #475569;

  &.primary {
    background: #8b5cf6;
    color: white;
    border-color: #8b5cf6;

    &:hover {
      background: #7c3aed;
      border-color: #7c3aed;
    }
  }

  &.secondary {
    background: white;
    color: #475569;

    &:hover {
      background: #f8fafc;
      border-color: #94a3b8;
    }
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  font-size: 18px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 200ms;

  &:hover {
    color: #64748b;
  }
`;

// ============ ICON COMPONENTS ============
const Icons = {
  CheckCircle: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 11.08v.5a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  ),
  AlertCircle: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  ),
  Info: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  ),
  X: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  Bell: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  ),
};

// ============ NOTIFICATION ITEM COMPONENT ============
const NotificationItem = ({ notification, onClose, onAction, onMarkAsRead }) => {
  const getIcon = (type) => {
    switch (type) {
      case 'success':
        return <Icons.CheckCircle />;
      case 'error':
      case 'warning':
        return <Icons.AlertCircle />;
      default:
        return <Icons.Info />;
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <NotificationItemWrapper $isClosing={notification.isClosing}>
      <NotificationItemContainer
        className={`${!notification.isRead ? 'unread' : ''} ${notification.type}`}
        onClick={() => onMarkAsRead?.(notification.id)}
      >
        <NotificationHeader>
          <NotificationTitleWrapper>
            <NotificationIcon $type={notification.type}>
              {getIcon(notification.type)}
            </NotificationIcon>
            <NotificationTitle>{notification.title}</NotificationTitle>
          </NotificationTitleWrapper>
          <NotificationTime>{formatTime(notification.timestamp)}</NotificationTime>
          <CloseButton onClick={(e) => {
            e.stopPropagation();
            onClose(notification.id);
          }}>
            <Icons.X />
          </CloseButton>
        </NotificationHeader>

        {notification.message && (
          <NotificationMessage>{notification.message}</NotificationMessage>
        )}

        {notification.details && (
          <NotificationDetails>
            {Object.entries(notification.details).map(([key, value]) => (
              <div key={key}>
                <div className="detail-label">{key}:</div>
                <div className="detail-value">{value}</div>
              </div>
            ))}
          </NotificationDetails>
        )}

        {notification.actions && notification.actions.length > 0 && (
          <NotificationActions>
            {notification.actions.map((action, idx) => (
              <ActionButton
                key={idx}
                className={action.variant || 'secondary'}
                onClick={(e) => {
                  e.stopPropagation();
                  onAction?.(notification.id, action);
                }}
              >
                {action.label}
              </ActionButton>
            ))}
          </NotificationActions>
        )}
      </NotificationItemContainer>
    </NotificationItemWrapper>
  );
};

// ============ NOTIFICATION PANEL COMPONENT ============
export const NotificationPanel = ({ isOpen, onClose, notifications = [] }) => {
  const [notificationList, setNotificationList] = useState(notifications);

  useEffect(() => {
    setNotificationList(notifications);
  }, [notifications]);

  const handleClose = useCallback((id) => {
    setNotificationList(prev =>
      prev.map(notif =>
        notif.id === id ? { ...notif, isClosing: true } : notif
      )
    );

    setTimeout(() => {
      setNotificationList(prev => prev.filter(notif => notif.id !== id));
    }, 300);
  }, []);

  const handleMarkAsRead = useCallback((id) => {
    setNotificationList(prev =>
      prev.map(notif =>
        notif.id === id ? { ...notif, isRead: true } : notif
      )
    );
  }, []);

  const handleAction = useCallback((id, action) => {
    action.onClick?.();
    handleClose(id);
  }, [handleClose]);

  const handleClearAll = useCallback(() => {
    setNotificationList(prev =>
      prev.map(notif => ({ ...notif, isClosing: true }))
    );

    setTimeout(() => {
      setNotificationList([]);
    }, 300);
  }, []);

  if (!isOpen) return null;

  const unreadCount = notificationList.filter(n => !n.isRead).length;

  return (
    <>
      <PanelOverlay onClick={onClose} />
      <PanelWrapper>
        <PanelHeader>
          <h2>
            <Icons.Bell />
            Notifications
            {unreadCount > 0 && ` (${unreadCount})`}
          </h2>
          <div style={{ display: 'flex', gap: '8px' }}>
            {notificationList.length > 0 && (
              <button className="clear-btn" onClick={handleClearAll}>
                Clear all
              </button>
            )}
            <button className="close-btn" onClick={onClose}>
              <Icons.X />
            </button>
          </div>
        </PanelHeader>

        <PanelContent>
          {notificationList.length === 0 ? (
            <EmptyState>
              <div className="empty-icon">
                <Icons.Bell />
              </div>
              <div className="empty-title">No notifications</div>
              <div className="empty-text">You're all caught up!</div>
            </EmptyState>
          ) : (
            notificationList.map(notification => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onClose={handleClose}
                onAction={handleAction}
                onMarkAsRead={handleMarkAsRead}
              />
            ))
          )}
        </PanelContent>
      </PanelWrapper>
    </>
  );
};

// ============ OVERLAY COMPONENT ============
const PanelOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(2px);
  z-index: 998;
  animation: ${fadeIn} 200ms ease-out;
`;

export default NotificationPanel;
